import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import useAiQuota from '@/hooks/useAiQuota';
import {
  User, MapPin, Calendar, Stethoscope, BookOpen, ExternalLink,
  Plus, Trash2, Save, Loader2, CheckCircle, XCircle, Clock, AlertCircle, ToggleRight, Star, Sparkles,
  Camera, Upload, Wallet, UserX, Crown, CreditCard, Download, Lock, AlertTriangle, Copy, Share2, MessageCircle,
  FileText, Image, Send, SpellCheck, Bell, BellOff, BarChart3, TrendingUp, Eye, EyeOff, Phone, Search,
  Newspaper, History, Check, X, Edit3, RefreshCw, ChevronDown, QrCode, Zap, Gift, Ticket, ClipboardList
} from 'lucide-react';
import BookingChat from '@/components/BookingChat';
import PinSettings from '@/components/dashboard/PinSettings';
import ProfilePublishedCelebration from '@/components/dashboard/ProfilePublishedCelebration';
import MonogramAvatar from '@/components/MonogramAvatar';
import VoiceMeter from '@/components/dashboard/VoiceMeter';
import ClinicSearchPicker from '@/components/ClinicSearchPicker';
import ClinicMap from '@/components/ClinicMap';
import HospitalAttachmentsSection from '@/components/dashboard/HospitalAttachmentsSection';
import {
  Field, ShareUrlBar, PushNotificationToggle, RedeemCouponCard,
  VoiceAuthenticityNotice, ChangePasswordSection,
  WhatsNewLaunchBanner, SetupBlocker, WebsiteScoreWidget,
  TierBadgeWithUpgrade, UpgradeBanner, SubscriptionWarning,
} from '@/components/dashboard/widgets/SharedWidgets';

const DAY_OPTIONS = [
  { value: 0, label: 'Monday' }, { value: 1, label: 'Tuesday' }, { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' }, { value: 4, label: 'Friday' }, { value: 5, label: 'Saturday' }, { value: 6, label: 'Sunday' },
];

const SCHEDULE_CATEGORIES = [
  { key: 'OPD', label: 'OPD' },
  { key: 'Consulting', label: 'Consulting' },
  { key: 'Surgery', label: 'Surgeries' },
  { key: 'MinorProcedure', label: 'Minor Procedures' },
];

export default function ScheduleTab() {
  const [schedules, setSchedules] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ location_id: '', days_of_week: [1], start_time: '10:00', end_time: '13:00', slot_duration_minutes: 15, block_type: 'opd', categories: ['OPD'] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    api.get('/dashboard/schedules').then(r => setSchedules(r.data)).catch(() => {});
    api.get('/dashboard/locations').then(r => setLocations(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleCategory = (key) => {
    setForm(f => {
      const has = (f.categories || []).includes(key);
      const next = has
        ? (f.categories || []).filter(c => c !== key)
        : [...(f.categories || []), key];
      return { ...f, categories: next };
    });
  };

  const toggleDay = (val) => {
    setForm(f => {
      const has = (f.days_of_week || []).includes(val);
      const next = has
        ? (f.days_of_week || []).filter(d => d !== val)
        : [...(f.days_of_week || []), val];
      return { ...f, days_of_week: next };
    });
  };

  const selectAllDays = () => setForm(f => ({ ...f, days_of_week: DAY_OPTIONS.map(d => d.value) }));
  const clearAllDays = () => setForm(f => ({ ...f, days_of_week: [] }));

  const handleAdd = async () => {
    if (!form.categories || form.categories.length === 0) {
      setError('Pick at least one service category for this block.');
      return;
    }
    if (!form.days_of_week || form.days_of_week.length === 0) {
      setError('Pick at least one day of the week.');
      return;
    }
    setSaving(true); setError('');
    try {
      // Apply same time block to every selected day, in parallel
      const { days_of_week, ...rest } = form;
      await Promise.all(days_of_week.map(d => api.post('/dashboard/schedules', { ...rest, day_of_week: d })));
      setShowForm(false);
      setForm(f => ({ ...f, categories: ['OPD'], days_of_week: [1] }));
      load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    await api.delete(`/dashboard/schedules/${id}`);
    load();
  };

  const getLocName = (id) => locations.find(l => l.id === id)?.name || id;
  const CATEGORY_STYLE = {
    OPD: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    Consulting: 'bg-sky-50 text-sky-700 border-sky-100',
    Surgery: 'bg-red-50 text-red-700 border-red-100',
    MinorProcedure: 'bg-amber-50 text-amber-700 border-amber-100',
  };

  // Group schedules by day for weekly view — sorted by start_time ascending per day
  const byDay = {};
  schedules.forEach(s => {
    if (!byDay[s.day_of_week]) byDay[s.day_of_week] = [];
    byDay[s.day_of_week].push(s);
  });
  Object.keys(byDay).forEach(d => {
    byDay[d].sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
  });

  return (
    <div className="space-y-4" data-testid="schedule-tab">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Weekly Schedule</h3>
          <p className="text-xs text-slate-500 mt-0.5">Define multiple time blocks per day at different locations. This pattern repeats weekly.</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-1.5 rounded-full px-4 py-2 bg-sky-600 text-white text-sm font-medium hover:bg-sky-700" data-testid="add-schedule-button">
          <Plus size={14} /> Add Block
        </button>
      </div>
      {locations.length === 0 && <div className="bg-amber-50 rounded-xl p-3 text-sm text-amber-700">Add a location first before setting up your schedule.</div>}
      {showForm && locations.length > 0 && (
        <div className="bg-white rounded-2xl border border-sky-200 p-5 space-y-4" data-testid="schedule-form">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="col-span-2 md:col-span-1">
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Location</label>
              <select className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none" value={form.location_id} onChange={e => setForm(f => ({ ...f, location_id: e.target.value }))} data-testid="sched-location">
                <option value="">Select Location</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <Field label="Start Time" value={form.start_time} onChange={v => setForm(f => ({ ...f, start_time: v }))} type="time" testid="sched-start" />
            <Field label="End Time" value={form.end_time} onChange={v => setForm(f => ({ ...f, end_time: v }))} type="time" testid="sched-end" />
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Consultation Period <span className="text-slate-400">(per slot)</span></label>
              <select className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none" value={form.slot_duration_minutes} onChange={e => setForm(f => ({ ...f, slot_duration_minutes: parseInt(e.target.value) }))} data-testid="sched-duration">
                {[10, 15, 20, 30, 45, 60, 90].map(m => (
                  <option key={m} value={m}>{m} min</option>
                ))}
              </select>
            </div>
          </div>

          {/* Multi-day checkbox picker — apply same time to multiple days */}
          <div data-testid="sched-days-picker">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-slate-600">Apply this time block to which days?</label>
              <div className="flex gap-1.5">
                <button type="button" onClick={selectAllDays} className="text-[11px] font-medium text-sky-600 hover:text-sky-700 underline-offset-2 hover:underline" data-testid="sched-days-all">All 7 Days</button>
                <span className="text-slate-300">·</span>
                <button type="button" onClick={clearAllDays} className="text-[11px] font-medium text-slate-500 hover:text-slate-700 underline-offset-2 hover:underline" data-testid="sched-days-clear">Clear</button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {DAY_OPTIONS.map(d => {
                const on = (form.days_of_week || []).includes(d.value);
                return (
                  <label key={d.value} className={`relative cursor-pointer select-none rounded-xl border-2 px-2 py-2.5 text-center transition-all ${on ? 'bg-sky-600 border-sky-600 text-white shadow-sm' : 'bg-white border-slate-200 text-slate-600 hover:border-sky-300'}`} data-testid={`sched-day-cb-${d.value}`}>
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggleDay(d.value)}
                      className="sr-only"
                    />
                    <span className="block text-[11px] font-bold tracking-wide uppercase">{d.label.slice(0, 3)}</span>
                    <span className={`block text-[10px] mt-0.5 ${on ? 'text-sky-100' : 'text-slate-400'}`}>{on ? '✓' : ''}</span>
                  </label>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-500 mt-2">Selected days: <span className="font-semibold text-slate-700">{(form.days_of_week || []).length}</span> — same time will be saved for each day in one click.</p>
          </div>
          {/* Multi-select service categories — patients see only slots matching the category they book for */}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Services available in this block (pick one or more)</label>
            <div className="flex flex-wrap gap-2" data-testid="sched-categories">
              {SCHEDULE_CATEGORIES.map(c => {
                const on = (form.categories || []).includes(c.key);
                return (
                  <button
                    type="button"
                    key={c.key}
                    onClick={() => toggleCategory(c.key)}
                    data-testid={`cat-${c.key}`}
                    className={`px-3 py-1.5 rounded-full border text-xs font-medium transition ${on ? 'bg-sky-600 border-sky-600 text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-sky-300'}`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button onClick={handleAdd} disabled={saving || !form.location_id} className="rounded-full px-5 py-2 bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50" data-testid="save-schedule-button">
            {saving ? 'Saving...' : `Save for ${(form.days_of_week || []).length} day${(form.days_of_week || []).length !== 1 ? 's' : ''}`}
          </button>
        </div>
      )}

      {/* Visual Weekly Grid */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="weekly-grid">
        <div className="grid grid-cols-7 border-b border-slate-100">
          {DAY_OPTIONS.map(d => (
            <div key={d.value} className="p-2 text-center text-xs font-medium text-slate-500 border-r border-slate-100 last:border-r-0">{d.label.slice(0, 3)}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 min-h-[120px]">
          {DAY_OPTIONS.map(d => {
            const dayScheds = byDay[d.value] || [];
            return (
              <div key={d.value} className="p-1.5 border-r border-slate-100 last:border-r-0 space-y-1">
                {dayScheds.length === 0 ? (
                  <p className="text-[10px] text-slate-300 text-center mt-4">No blocks</p>
                ) : (
                  dayScheds.map(s => {
                    const cats = s.categories || (s.block_type ? [s.block_type.toUpperCase()] : ['OPD']);
                    const primary = cats[0];
                    return (
                      <div key={s.id} className={`rounded-lg px-1.5 py-1 text-[10px] border ${CATEGORY_STYLE[primary] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                        <p className="font-medium truncate">{getLocName(s.location_id)}</p>
                        <p>{s.start_time}-{s.end_time}</p>
                        <p className="truncate opacity-80">{cats.length > 1 ? `${cats.length} services` : primary}</p>
                      </div>
                    );
                  })
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* List view — sorted by day then time ascending */}
      {schedules.length > 0 && (
        <div className="space-y-2">
          {[...schedules].sort((a, b) => {
            if (a.day_of_week !== b.day_of_week) return a.day_of_week - b.day_of_week;
            return (a.start_time || '').localeCompare(b.start_time || '');
          }).map(s => {
            const cats = s.categories || (s.block_type ? [s.block_type.toUpperCase()] : ['OPD']);
            return (
              <div key={s.id} className="bg-white rounded-xl border border-slate-200 p-3 flex items-center justify-between" data-testid={`sched-${s.id}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="w-9 h-9 rounded-lg bg-sky-50 flex items-center justify-center flex-shrink-0">
                    <Calendar size={14} className="text-sky-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">{DAY_OPTIONS.find(d => d.value === s.day_of_week)?.label} — {getLocName(s.location_id)}</p>
                    <p className="text-xs text-slate-500">{s.start_time} - {s.end_time} | {s.slot_duration_minutes}min slots</p>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {cats.map(c => (
                      <span key={c} className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${CATEGORY_STYLE[c] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>{c}</span>
                    ))}
                  </div>
                </div>
                <button onClick={() => handleDelete(s.id)} className="text-red-400 hover:text-red-600 p-1" data-testid={`del-sched-${s.id}`}><Trash2 size={14} /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
